import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/apiClient";
import { Bell, X, Check, Zap } from "lucide-react";
import { TimeAgo } from "@/components/sov";
import { useNavigate } from "react-router-dom";

const PRIO_COLOR = {
  sovereign: "#ff7a6e",
  critical:  "#ff9180",
  warn:      "#f5a73a",
  info:      "#9bb1c8",
};

const PulseBell = () => {
  const [open, setOpen] = useState(false);
  const [pulses, setPulses] = useState([]);
  const [summary, setSummary] = useState({ unack_total: 0, unack_by_priority: {} });
  const wsRef = useRef(null);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [list, sum] = await Promise.all([
        api.get("/pulse?limit=15"),
        api.get("/pulse/summary"),
      ]);
      setPulses(list.data.pulses);
      setSummary(sum.data);
    } catch {}
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  // Live WS subscription with graceful fallback to HTTP polling.
  // Some ingress configurations don't tunnel WebSocket upgrades through /api/*;
  // we attempt 3 reconnects with backoff then yield to the 30s poller.
  useEffect(() => {
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + "/api/ws/stream";
    let stopped = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 3;
    const connect = () => {
      if (stopped || attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { attempts = 0; }; // reset on success
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === "pulse") {
              setPulses((prev) => [m.data, ...prev].slice(0, 30));
              setSummary((s) => ({
                ...s,
                unack_total: (s.unack_total || 0) + 1,
                unack_by_priority: {
                  ...s.unack_by_priority,
                  [m.data.priority]: (s.unack_by_priority?.[m.data.priority] || 0) + 1,
                },
              }));
            }
          } catch {}
        };
        ws.onerror = () => {}; // suppress
        ws.onclose = () => {
          if (stopped || attempts >= MAX_ATTEMPTS) return;
          setTimeout(connect, 2000 * attempts);
        };
      } catch {
        if (!stopped && attempts < MAX_ATTEMPTS) setTimeout(connect, 2000 * attempts);
      }
    };
    connect();
    return () => {
      stopped = true;
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  const ack = async (id) => {
    try {
      await api.post(`/pulse/${id}/ack`);
      setPulses((p) => p.map((x) => x.id === id ? { ...x, ack: true } : x));
      setSummary((s) => ({ ...s, unack_total: Math.max(0, s.unack_total - 1) }));
    } catch {}
  };

  const ackAll = async () => {
    try {
      await api.post("/pulse/ack-all");
      refresh();
    } catch {}
  };

  const unread = summary.unack_total || 0;
  const sovereignCount = summary.unack_by_priority?.sovereign || 0;
  const criticalCount = summary.unack_by_priority?.critical || 0;
  const hasHighPrio = sovereignCount + criticalCount > 0;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center gap-2 px-3 py-1.5 border border-[color:var(--gold-line)] hover:border-[color:var(--gold-300)] transition"
        data-testid="pulse-bell"
      >
        <Bell size={13} className={hasHighPrio ? "text-[#ff7a6e]" : "text-gold-300"} />
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ivory-2">
          Pulse
        </span>
        {unread > 0 && (
          <span
            className="ml-1 font-mono text-[10px] px-1.5 py-px"
            style={{
              background: hasHighPrio ? "#ff7a6e" : "var(--gold-300)",
              color: "#0c0c11",
              borderRadius: 1,
              fontWeight: 700,
              boxShadow: hasHighPrio ? "0 0 12px rgba(255,122,110,0.6)" : "0 0 8px rgba(220,185,95,0.4)",
            }}
            data-testid="pulse-badge"
          >
            {unread}
          </span>
        )}
        {hasHighPrio && (
          <span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
            style={{ background: "#ff7a6e", boxShadow: "0 0 12px #ff7a6e", animation: "sev-pulse 1.4s infinite" }}
          />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed top-[64px] right-0 w-[420px] max-h-[calc(100vh-64px)] overflow-y-auto sov-panel z-50"
            style={{ background: "var(--obsidian-2)" }}
            data-testid="pulse-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--gold-line)]">
              <div className="flex items-center gap-2">
                <Zap size={13} className="text-gold-300" />
                <h3 className="font-display text-[12px] tracking-[0.22em] text-gold-100 uppercase">
                  Sentinel Pulse
                </h3>
                {unread > 0 && (
                  <span className="font-mono text-[9px] text-ivory-3 ml-2">{unread} unread</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={ackAll} className="btn-ghost" data-testid="pulse-ack-all">
                    <Check size={10} /> Ack All
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-ivory-3 hover:text-gold-200">
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* priority strip */}
            <div className="grid grid-cols-4 px-5 py-3 border-b border-[color:var(--gold-line)] gap-2">
              {["sovereign", "critical", "warn", "info"].map((p) => (
                <div key={p} className="text-center" data-testid={`pulse-prio-${p}`}>
                  <div className="font-display text-[14px]" style={{ color: PRIO_COLOR[p] }}>
                    {summary.unack_by_priority?.[p] || 0}
                  </div>
                  <div className="font-mono text-[8px] tracking-[0.2em] text-ivory-3 uppercase">{p}</div>
                </div>
              ))}
            </div>

            <div className="divide-y divide-[color:var(--gold-line)]">
              {pulses.length === 0 && (
                <div className="p-8 text-center font-serif italic text-[13px] text-ivory-3">
                  No pulses on the bridge. Sentinel is silent.
                </div>
              )}
              {pulses.map((p) => (
                <div
                  key={p.id}
                  className={`p-4 transition ${p.ack ? "opacity-50" : ""}`}
                  style={{
                    borderLeft: `2px solid ${PRIO_COLOR[p.priority]}`,
                  }}
                  data-testid={`pulse-row-${p.id.slice(0,6)}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span
                      className="font-mono text-[9px] tracking-[0.22em] uppercase"
                      style={{ color: PRIO_COLOR[p.priority] }}
                    >
                      {p.priority} · {p.category}
                    </span>
                    <TimeAgo iso={p.timestamp} />
                  </div>
                  <div className="font-display text-[13px] tracking-[0.04em] text-gold-100">
                    {p.title}
                  </div>
                  <div className="font-serif text-[12px] text-ivory-2 italic mt-1 leading-snug">
                    {p.message}
                  </div>
                  {!p.ack && (
                    <div className="flex justify-end mt-2">
                      <button onClick={() => ack(p.id)} className="btn-ghost" data-testid={`pulse-ack-${p.id.slice(0,6)}`}>
                        <Check size={10} /> Acknowledge
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-[color:var(--gold-line)] bg-[var(--obsidian-1)]/50">
              <button
                onClick={() => { setOpen(false); navigate("/pulse"); }}
                className="btn-ghost w-full justify-center"
                data-testid="pulse-open-deck"
              >
                Open Pulse Deck
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PulseBell;
